import { Checkbox, Divider, Radio, Stack, Typography } from '@mui/material';
import type { Role, User } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { RolePermissions } from '@/components/permissions/rolePermissions';
import { useRoles } from '@/contexts/useRoles';

export const RoleAccordionBody = ({
  roleToProjectsMappings,
  onChange,
  singleSelect,
}: {
  roleToProjectsMappings: User['roleToProjectsMappings'];
  onChange: (roleToProjectsMappings: User['roleToProjectsMappings']) => void;
  singleSelect?: boolean;
}) => {
  const { roles } = useRoles();

  const [selectedRole, setSelectedRole] = useState<Role>(
    roles.find((role) => !!roleToProjectsMappings[role.id]) || roles[0]
  );

  return (
    <Stack direction='row' className='pr-[40px]'>
      <Stack className='w-[260px] min-w-[260px]'>
        <Typography className='mb-[36px]'>Assigned roles</Typography>
        <Typography
          variant='body2'
          sx={{
            fontSize: '12px',
            textTransform: 'uppercase',
            cursor: 'default',
            margin: '12px 0 12px 0px',
          }}
        >
          Default roles
        </Typography>
        {roles
          .filter((role) => role.isBuiltInRole)
          .map((role) => (
            <RoleRow
              key={role.id}
              role={role}
              onChange={onChange}
              roleToProjectsMappings={roleToProjectsMappings}
              onClick={() => setSelectedRole(role)}
              isSelected={selectedRole.id === role.id}
              singleSelect={!!singleSelect}
            />
          ))}
        {roles.some((role) => !role.isBuiltInRole) && (
          <>
            <Divider className='my-[12px] ml-[-100px]' />
            <Typography
              variant='body2'
              sx={{
                fontSize: '12px',
                textTransform: 'uppercase',
                cursor: 'default',
                margin: '12px 0 12px 0px',
              }}
            >
              Custom roles
            </Typography>
          </>
        )}
        {roles
          .filter((role) => !role.isBuiltInRole)
          .map((role) => (
            <RoleRow
              key={role.id}
              role={role}
              onChange={onChange}
              roleToProjectsMappings={roleToProjectsMappings}
              onClick={() => setSelectedRole(role)}
              isSelected={selectedRole.id === role.id}
              singleSelect={!!singleSelect}
            />
          ))}
      </Stack>

      <Divider orientation='vertical' flexItem className='mr-[40px]' />
      <Stack gap='4px' flexGrow={1}>
        <Typography className='mb-[32px]'>
          <b>{selectedRole.name}</b>
          {` has these permissions:`}
        </Typography>
        <RolePermissions role={selectedRole} />
      </Stack>
    </Stack>
  );
};

const RoleRow = ({
  role,
  roleToProjectsMappings,
  onChange,
  onClick,
  isSelected,
  singleSelect,
}: {
  role: Role;
  roleToProjectsMappings: User['roleToProjectsMappings'];
  onChange: (roleToProjectsMappings: User['roleToProjectsMappings']) => void;
  onClick: () => void;
  isSelected: boolean;
  singleSelect: boolean;
}) => {
  return (
    <Stack
      direction='row'
      alignItems='center'
      onClick={onClick}
      className='py-[8px] cursor-pointer'
      sx={(theme) => ({
        borderRightStyle: 'solid',
        borderRightWidth: '3px',
        borderRightColor: isSelected
          ? 'var(--mui-palette-primary-main)'
          : 'transparent',

        '&:hover': {
          backgroundColor: theme.palette.background.primary,
        },
      })}
    >
      {!singleSelect && (
        <Checkbox
          checked={!!roleToProjectsMappings[role.id]}
          onChange={(event, checked) => {
            const roleToProjectsMappingsClone = { ...roleToProjectsMappings };

            if (checked) {
              roleToProjectsMappingsClone[role.id] = {};
            } else {
              // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
              delete roleToProjectsMappingsClone[role.id];
            }

            onChange(roleToProjectsMappingsClone);
          }}
        />
      )}
      {singleSelect && (
        <Radio
          checked={!!roleToProjectsMappings[role.id]}
          onChange={() => {
            const newMapping = {
              [role.id]: {},
            };

            onChange(newMapping);
          }}
        />
      )}

      <Typography className='ml-[12px]'>{role.name}</Typography>
    </Stack>
  );
};
